import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Bell } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await axios.post(`${API}/seed`);
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F2E8] pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="mb-12">
          <p className="text-[#4A7C29] text-[10px] font-semibold tracking-[0.2em] mb-2 font-['IBM_Plex_Sans_Condensed'] uppercase">TJGI — PRODUCT CATALOG</p>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-[0.05em] text-[#1A1A1A] font-['IBM_Plex_Sans_Condensed'] uppercase">
            ALL RELEASES
          </h1>
          <p className="text-[#5A5A5A] mt-3 max-w-2xl font-['IBM_Plex_Sans']">
            Limited production ultralight equipment. Inventory quantities are finite. When depleted, production cycles conclude.
          </p>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white aspect-square skeleton"></div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 bg-white border-2 border-[#E0DDD4]">
            <Bell className="w-12 h-12 text-[#5A5A5A] mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-2 font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">NO ACTIVE RELEASES</h2>
            <p className="text-[#5A5A5A] mb-6 font-['IBM_Plex_Sans']">Register for automated alerts when new products become available.</p>
            <Button 
              onClick={() => {
                const footer = document.querySelector('footer');
                footer?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="rounded-none bg-[#4A7C29] text-white font-semibold px-6 py-3 hover:bg-[#3D6822] font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider text-xs"
            >
              REGISTER FOR ALERTS
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Link 
                key={product.id}
                to={`/products/${product.id}`}
                className="group bg-white overflow-hidden border-2 border-[#E0DDD4] hover:border-[#4A7C29] transition-all"
                data-testid={`product-card-${product.id}`}
              >
                {/* Image */}
                <div className="relative aspect-square overflow-hidden bg-[#F5F2E8]">
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
                  />
                  
                  {/* Status Badge */}
                  {product.sold_out || product.stock === 0 ? (
                    <>
                      <div className="absolute inset-0 bg-black/40"></div>
                      <div className="absolute top-3 left-3 flex items-center gap-2 bg-[#1A1A1A] text-white px-3 py-1 text-[10px] font-semibold font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider">
                        <Clock className="w-3 h-3" />
                        DEPLETED
                      </div>
                    </>
                  ) : (
                    <div className="absolute top-3 left-3 flex items-center gap-2 bg-[#4A7C29] text-white px-3 py-1 text-[10px] font-semibold font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider">
                      AVAILABLE
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-5">
                  <p className="text-[9px] font-semibold tracking-[0.2em] text-[#4A7C29] mb-1 font-['IBM_Plex_Sans_Condensed'] uppercase">RELEASE 001</p>
                  <h3 className="font-semibold text-base text-[#1A1A1A] mb-2 group-hover:text-[#4A7C29] transition-colors font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
                    {product.name}
                  </h3>
                  <p className="text-[#5A5A5A] text-sm line-clamp-2 mb-4 font-['IBM_Plex_Sans']">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-[#1A1A1A] font-['IBM_Plex_Sans']">
                      ${product.price.toFixed(2)}
                    </span>
                    {product.weight && (
                      <span className="text-xs text-[#5A5A5A] bg-[#F5F2E8] px-2 py-1 font-['IBM_Plex_Mono'] border border-[#E0DDD4]">
                        {product.weight}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Coming Soon */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-5 py-2 mb-4">
            <Bell className="w-4 h-4" />
            <span className="text-xs font-medium font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider">ADDITIONAL RELEASES PENDING</span>
          </div>
          <p className="text-[#5A5A5A] text-sm font-['IBM_Plex_Sans']">
            Register for drop alerts to receive notification of future product availability.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Products;
